import React, { useState, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import Divider from "@material-ui/core/Divider";
import { useStyles } from "./styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import useHasPermissions from "../../../hooks/useHasPermissions";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import _ from "lodash";
import {selectContextEntity} from "../../../state/slices/CoreEntity";
import {useCoreEntityContext} from "../../../hooks/useCoreEntitySlice";
import useOnePass from "../../../hooks/useOnePass/useOnePass";
import ReportQueueIcon from "./ReportQueueIcon";
import {NavlistItem} from "../NavlistItem";
import {
    selectCompletedReportJobsCount,
    selectQueuedReportJobsCount
} from "../../../state/slices/ReportJobQueue/ReportJobQueue";

const NavListItems = (props) => {
  const { linksArray } = props;
  const classes = useStyles();
  const ldFeatureFlags = useFlags();
  const useCoreEntitySlice = useCoreEntityContext();
  const [childrenState, setChildrenState] = useState({});
  const { hasPermissions } = useHasPermissions();
  const isAdmin = useSelector((state) => state?.user?.isAdmin);
  const entityType = useSelector((state) => useCoreEntitySlice ? selectContextEntity(state)?.typeid : state.entities.Context.typeid);
  const currentUserID = useSelector(state => state.user?.UserID);
  const totalReportCompletedCount = useSelector(selectCompletedReportJobsCount);
  const totalReportQueuedCount = useSelector(selectQueuedReportJobsCount);

  const reportsStatusBadge = useMemo(() => {
    if (totalReportQueuedCount >= 1) {
      return {
        count: totalReportQueuedCount,
        type: 'warning'
      };
    }
    if (totalReportCompletedCount >= 1) {
      return {
        count: totalReportCompletedCount,
        type: 'success'
      };
    }
    return {
      count: 0,
      type: 'none'
    };

  }, [totalReportQueuedCount, totalReportCompletedCount]);


  const featureFlags = useFeatureFlags();
  const { isOnePassEnabled } = useOnePass(currentUserID); 

  function handleClick(parent) {
    let copy = childrenState;
    const found = copy[parent];
    if (!found) {
      copy[parent] = true;
    } else {
      copy[parent] = !found;
    }
    setChildrenState((prev) => ({ ...prev, copy }));
  }

  const permissionFilter = useCallback(
    (navItem) => {
      if (navItem.amiAdminOnly === true) {
        return isAdmin;
      }

      if (navItem.id === "validate-nav" && isOnePassEnabled) {
        return true;
      }

      if ((navItem.id === "one-pass-active-passes-nav" || navItem.id === "reports-nav" || navItem.id === "report-queue-nav") && isOnePassEnabled) {
        return true;
      }

      if (navItem.showIfContainsAnyPermission) {
        const any = navItem.showIfContainsAnyPermission;
        return hasPermissions(any, true);
      } else {
        const permissions = navItem.permissions;
        return !permissions || hasPermissions(permissions);
      }
    },
    [isAdmin, hasPermissions, isOnePassEnabled]
  );

  const featureFlagFilter = useCallback(
    (navItem) => {
      if (!navItem.flag) return true;

      const flags = Array.isArray(navItem.flag) ? navItem.flag : [navItem.flag];
      return flags.some(
        (f) =>
          _.includes(featureFlags, f) ||
          // TODO: remove once LaunchDarkly is sunset
          (_.has(ldFeatureFlags, f) && ldFeatureFlags[f])
      );
    },
    [featureFlags, ldFeatureFlags]
  );

  const entityTypeFilter = useCallback(
    (navItem) => {
      return !navItem.entityType || (navItem.entityType === entityType);
    },
    [entityType]
  );
  const validateFilter = useCallback(
    (navItem) => {
     if (navItem.id === "validate-nav") {
        const isOnePassFeatureEnabled =  _.includes(featureFlags, navItem.validateFlag)
        const hasRequiredPermissions =
        hasPermissions(["createprintedvalidations"]) || hasPermissions(["validatetickets"]);
        if (!hasRequiredPermissions && !isOnePassFeatureEnabled ) {
          return false;
        }
      }
      return true;
    },
    [hasPermissions]
  );

const customIconComponentMap = { 
  // Add more custom icons below by using the following example: sample1: <Sample1Icon />, sample2: <Sample2Icon />, 
  reportQueueIcon: <ReportQueueIcon />, 
}
    const navListStatusBadgeType = (id) =>{

        switch (id) {
            case "report-queue-nav":
                return reportsStatusBadge;
            default:
                return null;
        }

    }
  
  const getIconElement = (icon, customIcon, MaterialUIIcon, className, key, faIconsize, muiIconSize) => {
    if (customIcon) {
      return customIconComponentMap[customIcon] ?? null;
    } else if (icon) {  
      return <FontAwesomeIcon icon={icon} className={className} key={key} size={faIconsize}/>;
    } else if (MaterialUIIcon) {
      return <MaterialUIIcon className={className} key={key} fontSize={muiIconSize}/>;
    }
    return null;
  }

  const MenuItemChildren = (children, isOpen) => {
    return (
      <Collapse in={isOpen} unmountOnExit>
        <Divider />
        {Object.keys(children)
          .filter((key) => permissionFilter(children[key]))
          .filter((key) => featureFlagFilter(children[key]))
          .filter((key) => entityTypeFilter(children[key]))
          .map((key) => {
            const child = children[key];
            const IconElement = getIconElement(child.icon, child.customIcon, child.muiIcon, classes.navLink, key, "lg", "medium");
            return (
              <ListItem
                dense
                data-id={child.id}
                className={classes.navLink}
                component={NavLink}
                to={`${child.path}`}
                button
                key={key}
              >
                <ListItemIcon className={classes.subCategory}>
                  {IconElement}
                </ListItemIcon>
                <ListItemText key={key} primary={child.text} />
              </ListItem>
            );
          })}
      </Collapse>
    );
  };

  return Object.keys(linksArray)
    .filter((key) => permissionFilter(linksArray[key]))
    .filter((key) => featureFlagFilter(linksArray[key]))
    .filter((key) => entityTypeFilter(linksArray[key]))
    .filter((key) => validateFilter(linksArray[key]))
    .map((key) => {
      const { id, path, icon, muiIcon, text, children, customIcon } = linksArray[key];
      return (
        <React.Fragment key={key}>
            <NavlistItem
                id={id}
                path={path}
                customIcon={customIcon}
                muiIcon={muiIcon}
                icon={icon}
                text={text}
                childrennew={children}
                childrenState={childrenState}
                handleClick={handleClick}
                key={key}
                badgeStatus={navListStatusBadgeType(id)}
            ></NavlistItem>
          {!!children && MenuItemChildren(children, childrenState[text])}
        </React.Fragment>
      );
    });
};

export default NavListItems;
